import type { QSeverity, QVerdict, ResolutionStatus } from '../../../api/questionQualityApi';

/**
 * Shared display vocabulary for the Question Quality tab — one place where a
 * verdict or a severity becomes a colour, a glyph or a rank, so the By Rule and
 * By Question views can't drift into two different colour schemes for the same
 * ledger.
 */

export const SUBJECTS = ['chemistry', 'physics', 'maths', 'biology'];

export const sevColor: Record<QSeverity, string> = {
    critical: '#c0392b', major: '#d68910', minor: '#7f8c8d',
};

export const verdictColor: Record<QVerdict, string> = {
    fail: '#c0392b', pass: '#27ae60', unsure: '#8e44ad',
};

/** Worst-first ordering. Used for the severity sort and the "worst severity"
 *  rollup on a question. */
export const sevRank: Record<QSeverity, number> = { critical: 3, major: 2, minor: 1 };

/**
 * Resolution colours. `active` deliberately borrows nothing from the severity
 * ramp — it is not a judgement about the finding, only the absence of one — and
 * the settled states are the only green on the page, because they are the only
 * thing on it that means "done". The two AI states are in flight, so they take
 * their own hues rather than a shade of either end.
 */
export const resolutionColor: Record<ResolutionStatus, string> = {
    active: '#d68910',
    investigating: '#4a6cf7',
    'fix-needed-by-ai': '#7d3c98',
    'review-pending-after-ai-update': '#00838f',
    fixed: '#27ae60',
    // The same claim with evidence behind it, so the same green, deeper. A
    // separate hue would read as a different KIND of outcome; it is not one.
    'verified-fixed': '#1e7a45',
    'not-an-issue': '#7f8c8d',
};

/**
 * URL keys for the By Question list's own filters, prefixed so they cannot
 * collide with the tab (`tab`), the view (`qq`) or the subject (`qs`) above them.
 */
export const FILTER_PREFIX = 'qf_';
export const PAGE_PARAM = 'qf_page';

/**
 * Where one question's findings live. Shared, because both views point at the
 * same page — By Question from a row, By Rule from a failing question — and a
 * second copy of this string is how one of them ends up linking somewhere else.
 */
export function findingsPath(questionId: string, subject: string, from?: string): string {
    const params = new URLSearchParams({ subject });
    // The list's own query string, carried so Back returns to the filtered page
    // the reader left — including in a tab opened with cmd-click, where there is
    // no history to go back through.
    if (from) params.set('from', from);
    return `/question-quality/${encodeURIComponent(questionId)}?${params}`;
}
